import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Breadcrumb, Button } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { LS_AUTHORITY_KEY, currentYear, startYear } from '../../../core/constants/common';
import { BiPlus } from 'react-icons/bi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import { LoaderContext } from '../../../App';
import ReviewAndFinalize from './ReviewAndFinalize';
import InventoryOverview from './InventoryOverview';
import Nodataimg from "../../../images/Assessment.png";
import useHasPermission from '../../../core/hooks/useHasPermission';

const GHGInventory = ({ t }) => {
    // All hooks must be called first, in the same order every render
    const [searchParams] = useSearchParams();
    const { setLoaderDisplay } = useContext(LoaderContext);
    const navigate = useNavigate();
    
    // Initialize filter state with a function to avoid accessing searchParams multiple times
    const [filter, setFilter] = useState(() => {
        const passYear = searchParams.get('year') ?? null;
        const passTable = searchParams.get('table') ?? null;
        const year = new Date().getFullYear();
        return {
            year: passYear ?? `${year}`,
            emissionTable: passTable ?? 'GHG'
        };
    });
    const [finalized, setfinalized] = useState(false);
    const [organizationalBoundary, setOrganizationalBoundary] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const [hasSelSources, setHasSelSources] = useState(false);
    const { hasGHGEmissionsUpdatePermission } = useHasPermission();

    // Initialize companyId from localStorage synchronously
    const [companyId] = useState(() => {
        try {
            const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
            return lsDat?.profile?.company?.id || null;
        } catch {
            return null;
        }
    });

    // Memoize yearOptions to avoid recreating on every render
    const yearOptions = useMemo(() => {
        const options = [];
        for (let i = currentYear; i >= startYear; i--) {
            options.push(<option value={i} key={i}>{i}</option>);
        }
        return options;
    }, []);

    useEffect(() => {
      // getSummary();
      getSummaryAll();
        // handleClearYear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.year]);

    useEffect(() => {
        getDataSet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getDataSet = () => {
        // setLoaderDisplay(true);
        GHGEmissionService.getDataSet(companyId)
            .then((response) => {
                // setLoaderDisplay(false);
                setDatasets(response.data.data);
            })
            .catch(() => {
                // setLoaderDisplay(false);
            });
    }

    const handleClearYear = () => {
        searchParams.delete('year');
        const currentYear = new Date().getFullYear();
        setFilter({
            year: `${currentYear}`,
            emissionTable: 'GHG'
        });
    };

    const hasAnsweredQuestions = (data) => {
        for (const scope of data) {
            for (const category of scope.categories) {
                for (const question of category.questions) {
                    if (question.answer === "Yes" || question.answer === "No") {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const hasSelectedSources = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return false;
        }
        return Object.values(data).some(scope => {
            if (!scope || typeof scope !== 'object' || Array.isArray(scope)) {
                return false;
            }
            return Object.values(scope).some(category => {
                if (!Array.isArray(category)) {
                    return false;
                }
                return category.some(source =>
                    source && (
                        source.alreadySelected === true ||
                        (source.factors && Array.isArray(source.factors) && source.factors.some(factor => factor.alreadySelected === true))
                    )
                );
            });
        });
    };

    const flattenEmissionSources = (emissionSourceData) => {
        // Structure: { "GHG": { "Scope 1": { "Category": [...] } }, "DEFRA": {...}, "CEA": {...} }
        // Need to flatten to: { "Scope 1": { "Category": [...] }, "Scope 2": {...} }
        const flattened = {};

        if (!emissionSourceData || typeof emissionSourceData !== 'object' || Array.isArray(emissionSourceData)) {
            return flattened;
        }

        Object.entries(emissionSourceData).forEach(([dataset, scopes]) => {
            if (!scopes || typeof scopes !== 'object' || Array.isArray(scopes)) {
                return;
            }
            
            Object.entries(scopes).forEach(([scopeName, categories]) => {
                if (!categories || typeof categories !== 'object' || Array.isArray(categories)) {
                    return;
                }
                
                if (!flattened[scopeName]) {
                    flattened[scopeName] = {};
                }
                
                Object.entries(categories).forEach(([categoryName, sources]) => {
                    if (!Array.isArray(sources)) {
                        return;
                    }
                    
                    if (!flattened[scopeName][categoryName]) {
                        flattened[scopeName][categoryName] = [];
                    }
                    // Merge sources from different datasets
                    flattened[scopeName][categoryName] = [
                        ...flattened[scopeName][categoryName],
                        ...sources
                    ];
                });
            });
        });

        return flattened;
    };

    const getSummary = () => {
        // setLoaderDisplay(true);
        const date = filter?.year + '-01-01';
        GHGEmissionService.getSummaryData(filter.emissionTable ?? 'GHG', companyId, date)
            .then((response) => {
                setLoaderDisplay(false);
                setfinalized(response.data.data['finalized']);
                setOrganizationalBoundary(response.data.data['organizationalBoundary']);
                setHasAnswered(hasAnsweredQuestions(response.data.data['operationalBoundary']));
                setHasSelSources(hasSelectedSources(response.data.data['emissionSource']))
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const getSummaryAll = () => {
        // setLoaderDisplay(true);
        const date = filter?.year + '-01-01';
        GHGEmissionService.getSummaryAll(companyId, date)
            .then((response) => {
                setLoaderDisplay(false);
                setfinalized(response.data.data['finalized'] || false);
                setOrganizationalBoundary(response.data.data['organizationalBoundary'] || []);
                setHasAnswered(hasAnsweredQuestions(response.data.data['operationalBoundary'] || []));
                
                // Handle new structure: { "GHG": { "Scope 1": {...} }, "DEFRA": {...}, "CEA": {...} }
                // The API returns emissionSourceAll with dataset keys (GHG, DEFRA, CEA)
                const emissionSourceRaw = response.data.data['emissionSourceAll'] || {};
                const emissionSource = flattenEmissionSources(emissionSourceRaw);
                
                // Check if there are any selected sources in the flattened structure
                setHasSelSources(hasSelectedSources(emissionSource));
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const gotoAdd = () => {
        navigate(`/company_admin/add_ghg_inventory?year=` + filter.year + `&table=` + filter.emissionTable);
    }


    return (
        <div className='GHGInventory'>
            <div className="List_header mb-1">
                <div>
                    <Breadcrumb className='h5'>
                        <Breadcrumb.Item>{t("GHGEmissions.label")}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t("GHGInventory.label")}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className='GHGInventory_btn_head'>
                    {
                        !finalized && (organizationalBoundary.length > 0 || hasAnswered || hasSelSources) &&
                        <>
                            <span className='select_status02 badge bg-badge-Draft rounded-pill'>{t('InProgress.label')}</span>
                            <Button variant="primary" className="px-3 mx-2" onClick={() => gotoAdd()}>
                                {t("Resume.label")}
                            </Button>
                        </>
                    }
                    {
                        finalized &&
                        <>
                            <span className='select_status02 badge bg-badge-Finalized rounded-pill'>{t('Completed.label')}</span>
                        </>
                    }
                    <select className="form-select  w-auto" value={filter.year}
                        onChange={(e) => {
                            setFilter({ ...filter, year: e.target.value });
                        }}>
                        {yearOptions}
                    </select>
                    {/* <select className="form-select  w-auto" value={filter.emissionTable}
                        onChange={(e) => {
                            setFilter({ ...filter, emissionTable: e.target.value });
                        }}>
                        {datasets.map((table) => (
                            <option key={table.id} value={table.name}>
                                {table.name}
                            </option>
                        ))}
                    </select> */}
                </div>
            </div>
            <div className='row m-0'>
                {
                    !finalized && organizationalBoundary.length === 0 && !hasAnswered && !hasSelSources &&
                    <div className='col-lg-12 px-0 py-2 text-center'>
                        <div className='noDataCard'>
                            <div>
                                <div className='Nodataimg_box'>
                                    <img src={Nodataimg} />
                                </div>
                                <h5 className='py-3'>
                                    No GHG inventory has been created for the year {filter.year}. Click 'New' to begin the setup.
                                </h5>
                                <Button variant="primary" className="px-3 mx-2" hidden={!hasGHGEmissionsUpdatePermission()} onClick={() => gotoAdd()}>
                                    <BiPlus className="mr-2" /> {t("New.label")}
                                </Button>
                            </div>
                        </div>

                    </div>
                }
                {
                    !finalized && (organizationalBoundary.length > 0 || hasAnswered || hasSelSources) &&
                    <>
                        <InventoryOverview filterData={filter} noShowFooter={true} />
                    </>
                }
                {
                    finalized && <div className='col-lg-12 px-0'>
                        <ReviewAndFinalize filterData={filter} noShowFooter={false} />
                    </div>
                }
            </div>
        </div >
    )
};
export default withTranslation()(GHGInventory);